import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { 
  Search, Briefcase, Settings, BookOpen, FileText, BarChart3, 
  Sparkles, Layers, Plus, ArrowRight, Menu, X, User, Star, TrendingUp,
   Zap, Target, Award, Users
} from 'lucide-react';

// Hook pour les animations d'intersection
const useIntersectionObserver = (options) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const targetRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
    }, options);

    const currentRef = targetRef.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [options]);

  return [targetRef, isIntersecting];
};

// Composant Sidebar moderne
const ModernSidebar = ({ isOpen, setIsOpen, user, navigate }) => {
  const quickActions = [
    { icon: Plus, label: 'Nouveau Projet', path: '/create-project', color: 'text-emerald-500' },
    { icon: Search, label: 'Opportunités', path: '/feed', color: 'text-blue-500' },
    { icon: Layers, label: 'Workspace', path: '/workspace', color: 'text-orange-500' },
    { icon: User, label: 'Mon Profil', path: '/profile', color: 'text-purple-500' }
  ];

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full w-80 bg-white/95 backdrop-blur-xl border-r border-white/20 
        shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:z-auto lg:fixed lg:left-0
      `}>
        {/* Header Fixe */}
        <div className="p-6 border-b border-gray-200/50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setIsOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Contenu Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {/* User Info */}
          <div className="p-6 border-b border-gray-200/50">
            {user && (
              <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold">
                  {user.nom?.charAt(0)}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{user.nom}</p>
                  <p className="text-sm text-gray-500">{user.role}</p>
                </div>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="p-6 border-b border-gray-200/50">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-500" />
              Actions Rapides
            </h3>
            <div className="space-y-2">
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    navigate(action.path);
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-all duration-200 group"
                >
                  <action.icon className={`w-5 h-5 ${action.color} group-hover:scale-110 transition-transform`} />
                  <span className="text-gray-700 group-hover:text-gray-900">{action.label}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </div>
          </div>

          {/* Favoris */}
          <div className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              En Favoris
            </h3>
            <div className="space-y-3">
              {(() => {
                try {
                  const favorites = JSON.parse(localStorage.getItem('djamann_favorite_opportunities') || '[]');
                  if (favorites.length === 0) {
                    return (
                      <p className="text-xs text-gray-500 text-center py-2">
                        Aucun favoris pour le moment
                      </p>
                    );
                  }
                  return favorites.slice(0, 3).map((fav, idx) => (
                    <div 
                      key={idx} 
                      onClick={() => {
                        const projectId = fav.project_id || fav.projectId || fav.projet_id;
                        const taskId = fav.id || fav.task_id || fav.taskId;
                        if (projectId && taskId) {
                          navigate(`/project/${projectId}?task=${taskId}`);
                        }
                      }}
                      className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <Sparkles className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 font-medium truncate group-hover:text-indigo-600 transition-colors">{fav.titre || 'Sans titre'}</p>
                        <p className="text-xs text-gray-500 truncate">{fav.project_titre || 'Projet'}</p>
                      </div>
                    </div>
                  ));
                } catch (e) {
                  return (
                    <p className="text-xs text-gray-500 text-center py-2">
                      Erreur lors du chargement
                    </p>
                  );
                }
              })()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Composant Carte Menu Moderne
const ModernMenuCard = ({ item, onNavigate, index }) => {
  const [ref, isVisible] = useIntersectionObserver({ threshold: 0.1 });
  const [isHovered, setIsHovered] = useState(false);
  
  const IconComponent = item.icon;

  return (
    <div
      ref={ref}
      onClick={() => onNavigate(item.path)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        group relative overflow-hidden rounded-2xl sm:rounded-3xl cursor-pointer
        transition-all duration-700 ease-out transform
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
        hover:scale-105 hover:-translate-y-2
        ${isHovered ? 'shadow-2xl shadow-purple-500/25' : 'shadow-lg'}
      `}
      style={{
        animationDelay: `${index * 100}ms`,
      }}
    >
      {/* Fond avec effet parallax */}
      <div className={`
        absolute inset-0 bg-gradient-to-br ${item.color}
        transition-transform duration-700 ease-out
        ${isHovered ? 'scale-110 rotate-1' : 'scale-100'}
      `}>
        {/* Pattern géométrique animé */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 200 200">
            <defs>
              <pattern id={`pattern-${item.id}`} x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="20" cy="20" r="2" fill="white" opacity="0.3" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#pattern-${item.id})`} />
          </svg>
        </div>
        
        {/* Effet de brillance */}
        <div className={`
          absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent
          transform -skew-x-12 transition-transform duration-1000
          ${isHovered ? 'translate-x-full' : '-translate-x-full'}
        `} />
      </div>

      {/* Contenu */}
      <div className="relative h-40 sm:h-48 md:h-56 lg:h-64 p-4 sm:p-6 md:p-8 flex flex-col justify-between z-10">
        {/* Header avec animation de pulsation */}
        <div className="flex items-start justify-between">
          <div className={`
            p-2 sm:p-3 md:p-4 bg-white/20 backdrop-blur-md rounded-lg sm:rounded-xl md:rounded-2xl 
            transition-all duration-500
            ${isHovered ? 'bg-white/30 scale-110 rotate-12' : 'scale-100 rotate-0'}
          `}>
            <IconComponent className="w-5 h-5 sm:w-6 md:w-8 text-white" strokeWidth={1.5} />
          </div>
          
          {/* Badge de statut */}
          <div className={`
            opacity-0 transition-all duration-500 text-xs sm:text-sm
            ${isHovered ? 'opacity-100 translate-x-0' : 'translate-x-4'}
          `}>
            <div className="flex items-center gap-1 px-2 sm:px-3 py-1 bg-white/20 backdrop-blur-md rounded-full">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-white font-medium">Actif</span>
            </div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="space-y-2 sm:space-y-3 md:space-y-4">
          <div>
            <h3 className={`
              text-lg sm:text-xl md:text-2xl font-bold text-white mb-1 sm:mb-2 md:mb-3
              transition-transform duration-500
              ${isHovered ? 'translate-y-[-4px]' : 'translate-y-0'}
            `}>
              {item.title}
            </h3>
            <p className={`
              text-xs sm:text-sm text-white/90 leading-relaxed
              transition-all duration-500
              ${isHovered ? 'text-white' : 'text-white/90'}
            `}>
              {item.description}
            </p>
          </div>

          {/* Footer avec call-to-action */}
          <div className={`
            flex items-center justify-between
            transition-all duration-500 text-xs sm:text-sm
            ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-60 translate-y-2'}
          `}>
            <span className="text-white/80 font-medium px-2 py-0.5 sm:px-3 sm:py-1 bg-white/10 rounded-full">
              Cliquez
            </span>
            <ArrowRight className={`
              w-4 h-4 sm:w-5 md:w-6 text-white transition-transform duration-500
              ${isHovered ? 'translate-x-2 scale-110' : 'translate-x-0 scale-100'}
            `} />
          </div>
        </div>
      </div>

      {/* Effet de lueur au hover */}
      <div className={`
        absolute inset-0 transition-opacity duration-500
        ${isHovered ? 'opacity-100' : 'opacity-0'}
      `}>
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/20 blur-2xl rounded-full animate-pulse" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 blur-xl rounded-full animate-pulse" style={{ animationDelay: '500ms' }} />
      </div>
    </div>
  );
};

// Composant Stats avec animation
const AnimatedStats = ({ stats }) => {
  const [ref, isVisible] = useIntersectionObserver({ threshold: 0.5 });
  const [counters, setCounters] = useState(stats.map(() => 0));

  useEffect(() => {
    if (isVisible) {
      stats.forEach((stat, index) => {
        const targetValue = typeof stat.value === 'number' ? stat.value : parseInt(stat.value.replace(/[^0-9]/g, ''));
        const increment = targetValue / 50;
        let current = 0;

        const timer = setInterval(() => {
          current += increment;
          if (current >= targetValue) {
            setCounters(prev => {
              const newCounters = [...prev];
              newCounters[index] = targetValue;
              return newCounters;
            });
            clearInterval(timer);
          } else {
            setCounters(prev => {
              const newCounters = [...prev];
              newCounters[index] = Math.floor(current);
              return newCounters;
            });
          }
        }, 50);

        return () => clearInterval(timer);
      });
    }
  }, [isVisible, stats]);

  return (
    <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {stats.map((stat, index) => (
        <div key={index} className="group relative">
          <div className="bg-white/60 backdrop-blur-xl rounded-2xl p-8 border border-white/20 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-2">
            <div className="flex items-center justify-between mb-4">
              <stat.icon className={`w-8 h-8 ${stat.color} group-hover:scale-110 transition-transform duration-500`} />
              <TrendingUp className="w-5 h-5 text-green-500 opacity-60" />
            </div>
            <div className="space-y-2">
              <div className={`text-4xl font-bold bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`}>
                {typeof stat.value === 'string' ? stat.value : counters[index].toLocaleString()}
                {stat.suffix && <span className="text-2xl">{stat.suffix}</span>}
              </div>
              <p className="text-gray-600 font-medium">{stat.label}</p>
              <div className={`h-1 bg-gradient-to-r ${stat.gradient} rounded-full opacity-60`} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Composant principal
const HomePage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Stats animées
  const stats = [
    { 
      icon: Users, 
      value: 50000, 
      suffix: '+',
      label: 'Utilisateurs Actifs', 
      color: 'text-blue-500',
      gradient: 'from-blue-500 to-cyan-500'
    },
    { 
      icon: Briefcase, 
      value: 2400, 
      suffix: '+',
      label: 'Projets Actifs', 
      color: 'text-purple-500',
      gradient: 'from-purple-500 to-pink-500'
    },
    { 
      icon: Award, 
      value: '99%', 
      label: 'Taux de Satisfaction', 
      color: 'text-green-500',
      gradient: 'from-green-500 to-emerald-500'
    },
    { 
      icon: Target, 
      value: 1800, 
      suffix: '+',
      label: 'Services Disponibles', 
      color: 'text-orange-500',
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  // Sections en vedette mises à jour
  const featuredSections = [
    {
      id: 'opportunities',
      title: 'Découvrez les\nOpportunités',
      emoji: '✨',
      color: 'from-rose-500 via-pink-500 to-fuchsia-600',
      description: 'Projets exclusifs et opportunités d\'emploi adaptés à votre profil',
      path: '/feed',
      stats: '2,400+ offres actives',
      metrics: { views: '12k', engagement: '85%' }
    },
    {
      id: 'services',
      title: 'Services\nProfessionnels',
      emoji: '💼',
      color: 'from-blue-500 via-cyan-500 to-indigo-600',
      description: 'Freelances et professionnels vérifiés pour tous vos besoins',
      path: '/services',
      stats: '1,800+ services vérifiés',
      metrics: { rating: '4.9/5', completion: '98%' }
    },
    {
      id: 'collaboration',
      title: 'Collaboration\n& Équipes',
      emoji: '🤝',
      color: 'from-purple-500 via-violet-500 to-indigo-600',
      description: 'Trouvez et collaborez avec les meilleurs talents',
      path: '/projects',
      stats: '3,100+ projets en cours',
      metrics: { success: '94%', network: '15k+' }
    },
    {
      id: 'marketplace',
      title: 'Marketplace\nInnovant',
      emoji: '🛍️',
      color: 'from-emerald-500 via-teal-500 to-cyan-600',
      description: 'Shopping et e-commerce nouvelle génération',
      path: '/boutiques',
      stats: '500+ boutiques partenaires',
      metrics: { growth: '+156%', sales: '€2M+' }
    }
  ];

  // Menu items avec nouvelles catégories
  const menuItems = [
    {
      id: 'opportunities',
      title: 'Opportunités',
      description: 'Découvrez des opportunités de projets et d\'emploi adaptées à votre profil',
      icon: Sparkles,
      path: '/feed',
      color: 'from-rose-500 to-pink-600',
      category: 'Exploration'
    },
    {
      id: 'services',
      title: 'Services Pro',
      description: 'Marketplace de services professionnels premium',
      icon: Briefcase,
      path: '/services',
      color: 'from-blue-500 to-cyan-600',
      category: 'Business'
    },
    {
      id: 'projects',
      title: 'Collaborations',
      description: 'Projets collaboratifs et gestion d\'équipes',
      icon: BookOpen,
      path: '/projects',
      color: 'from-purple-500 to-indigo-600',
      category: 'Collaboration'
    },
    {
      id: 'applications',
      title: 'Candidatures',
      description: 'Suivi intelligent de vos candidatures',
      icon: FileText,
      path: '/my-applications',
      color: 'from-green-500 to-emerald-600',
      category: 'Gestion'
    }
  ];

  // Menu items pour auteurs avec nouvelles fonctionnalités
  const authorMenuItems = user?.role === 'author' || user?.role === 'admin' ? [
    {
      id: 'create-project',
      title: 'Créer un projet',
      description: 'Créez des projets avec des outils avancés',
      icon: Plus,
      path: '/create-project',
      color: 'from-cyan-500 to-blue-600',
      category: 'Création'
    },
    {
      id: 'my-services',
      title: 'Hub Services',
      description: 'Gérez votre portfolio de services',
      icon: Settings,
      path: '/my-services',
      color: 'from-amber-500 to-orange-600',
      category: 'Gestion'
    },
    {
      id: 'workspace',
      title: 'Workspace Pro',
      description: 'Interface unifiée pour gérer votre business',
      icon: Layers,
      path: '/workspace',
      color: 'from-teal-500 to-cyan-600',
      category: 'Business'
    },
    {
      id: 'reports',
      title: 'Rapports & Statistiques',
      description: 'Analyses et statistiques détaillées',
      icon: BarChart3,
      path: '/workspace',
      color: 'from-indigo-500 to-purple-600',
      category: 'Données'
    }
  ] : [];

  // Tous les éléments du menu
  const allMenuItems = [...menuItems, ...authorMenuItems];
  const filteredItems = allMenuItems.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Gestionnaires d'événements
  const handleNavigate = (path) => navigate(path);
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim() && filteredItems.length > 0) {
      handleNavigate(filteredItems[0].path);
    }
  };

  // Auto-slide pour les sections featured
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredSections.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [featuredSections.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* Styles CSS avancés */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(1deg); }
          66% { transform: translateY(-5px) rotate(-1deg); }
        }
        
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.4); }
          50% { box-shadow: 0 0 40px rgba(99, 102, 241, 0.8); }
        }
        
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        .float-animation { animation: float 6s ease-in-out infinite; }
        .pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
        .gradient-shift { 
          background-size: 200% 200%;
          animation: gradient-shift 8s ease infinite;
        }
        
        .glass-effect {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        .neo-shadow {
          box-shadow: 
            20px 20px 40px rgba(0, 0, 0, 0.1),
            -20px -20px 40px rgba(255, 255, 255, 0.8);
        }
      `}</style>

      {/* Background Éléments Décoratifs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl float-animation" />
        <div className="absolute top-1/2 -right-40 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl float-animation" style={{ animationDelay: '2s' }} />
        <div className="absolute -bottom-40 left-1/2 w-96 h-96 bg-pink-400/20 rounded-full blur-3xl float-animation" style={{ animationDelay: '4s' }} />
        
        {/* Grille décorative */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgb(99 102 241) 1px, transparent 0)`,
            backgroundSize: '50px 50px'
          }} />
        </div>
      </div>

      {/* Layout Principal */}
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <ModernSidebar 
          isOpen={sidebarOpen} 
          setIsOpen={setSidebarOpen} 
          user={user}
          navigate={navigate}
        />

        {/* Contenu Principal */}
        <div className="flex-1 lg:ml-80 w-full overflow-hidden">
          {/* Header Mobile */}
          <div className="lg:hidden bg-white/80 backdrop-blur-xl border-b border-white/20 px-3 sm:px-4 py-2 sm:py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-lg sm:rounded-xl hover:bg-gray-100 transition-colors"
              >
                <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              <h1 className="font-bold text-base sm:text-lg">Accueil</h1>

            </div>
          </div>


            {/* Sections Featured avec Slider Avancé */}
            <div className="mb-16 sm:mb-20 md:mb-24">
              



              {/* Slider Container */}
              <div className="relative">
                {/* Main Slider */}
                <div className="overflow-hidden rounded-3xl">
                  <div 
                    className="flex transition-transform duration-700 ease-in-out"
                    style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                  >
                    {featuredSections.map((section, idx) => (
                      <div key={section.id} className="w-full flex-shrink-0 px-2">
                        <div
                          onClick={() => navigate(section.path)}
                          className="group cursor-pointer relative h-64 sm:h-72 md:h-80 lg:h-96 rounded-2xl sm:rounded-3xl overflow-hidden"
                        >
                          {/* Background with advanced gradient */}
                          <div className={`
                            absolute inset-0 bg-gradient-to-br ${section.color}
                            group-hover:scale-105 transition-transform duration-700
                          `}>
                            {/* Animated pattern overlay */}
                            <div className="absolute inset-0 opacity-20">
                              <svg className="w-full h-full" viewBox="0 0 400 400">
                                <defs>
                                  <pattern id={`grid-${section.id}`} width="40" height="40" patternUnits="userSpaceOnUse">
                                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" opacity="0.3" />
                                  </pattern>
                                </defs>
                                <rect width="100%" height="100%" fill={`url(#grid-${section.id})`} />
                              </svg>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="relative h-full flex items-center justify-between p-6 sm:p-8 md:p-10 lg:p-12 z-10">
                            {/* Left Content */}
                            <div className="flex-1 max-w-lg">
<div className="text-6xl sm:text-7xl md:text-8xl mb-4 sm:mb-6 group-hover:scale-125 transition-transform duration-500">
                          {section.emoji}
                        </div>
                        <h3 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 sm:mb-6 leading-tight whitespace-pre-line group-hover:translate-y-[-8px] transition-transform duration-500">
                          {section.title}
                        </h3>
                        <p className="text-white/90 text-base sm:text-lg md:text-xl mb-6 sm:mb-8 leading-relaxed group-hover:text-white transition-colors">
                                {section.description}
                              </p>
                              
                              {/* Metrics */}
                              <div className="flex items-center gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8 flex-wrap text-xs sm:text-sm md:text-base">
                                <div className="text-center">
                                  <div className="text-lg sm:text-xl md:text-2xl font-bold text-white">{section.stats}</div>
                                  <div className="text-white/70 text-xs sm:text-sm">Disponibles</div>
                                </div>
                                {section.metrics && Object.entries(section.metrics).map(([key, value], metricIdx) => (
                                  <div key={metricIdx} className="text-center">
                                    <div className="text-lg sm:text-xl md:text-2xl font-bold text-white">{value}</div>
                                    <div className="text-white/70 text-xs sm:text-sm capitalize">{key}</div>
                                  </div>
                                ))}
                              </div>

                              {/* CTA Button */}
                              <button className="group-hover:scale-110 transition-transform duration-300 px-6 sm:px-7 md:px-8 py-3 sm:py-3.5 md:py-4 bg-white/20 hover:bg-white/30 backdrop-blur-md text-white font-semibold text-sm sm:text-base rounded-lg sm:rounded-2xl border border-white/30 w-full sm:w-auto">
                                <span className="flex items-center justify-center gap-2">
                                  Découvrir maintenant
                                  <ArrowRight className="w-4 h-4 sm:w-5 group-hover:translate-x-2 transition-transform" />
                                </span>
                              </button>
                            </div>

                            {/* Right Visual Element */}
                            <div className="hidden lg:block">
                              <div className="w-64 h-64 relative">
                                <div className="absolute inset-0 bg-white/10 rounded-full blur-3xl animate-pulse" />
                                <div className="absolute inset-4 bg-white/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }} />
                                <div className="absolute inset-8 bg-white/30 rounded-full blur-xl animate-pulse" style={{ animationDelay: '2s' }} />
                              </div>
                            </div>
                          </div>

                          {/* Hover Effects */}
                          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 blur-3xl rounded-full animate-pulse" />
                            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 blur-2xl rounded-full animate-pulse" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Slider Controls */}
                <div className="flex items-center justify-center gap-3 sm:gap-4 mt-6 sm:mt-8 flex-wrap px-2">
                  {/* Dots Indicator */}
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    {featuredSections.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentSlide(idx)}
                        className={`
                          rounded-full transition-all duration-300 
                          w-2.5 h-2.5 sm:w-3 sm:h-3
                          ${currentSlide === idx 
                            ? 'bg-indigo-500 w-6 sm:w-8' 
                            : 'bg-gray-300 hover:bg-gray-400'
                          }
                        `}
                      />
                    ))}
                  </div>

                  {/* Navigation Arrows */}
                  <div className="flex items-center gap-1.5 sm:gap-2 ml-4 sm:ml-6">
                    <button
                      onClick={() => setCurrentSlide(currentSlide === 0 ? featuredSections.length - 1 : currentSlide - 1)}
                      className="p-2 sm:p-3 bg-white/80 backdrop-blur-md rounded-lg sm:rounded-xl border border-white/30 hover:bg-white transition-colors shadow-lg hover:scale-110"
                    >
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 rotate-180" />
                    </button>
                    <button
                      onClick={() => setCurrentSlide(currentSlide === featuredSections.length - 1 ? 0 : currentSlide + 1)}
                      className="p-2 sm:p-3 bg-white/80 backdrop-blur-md rounded-lg sm:rounded-xl border border-white/30 hover:bg-white transition-colors shadow-lg hover:scale-110"
                    >
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                    </button>
                  </div>
                </div>
              </div>
            </div>





          {/* Contenu Principal */}
          <div className="relative z-10 w-full px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-8 md:py-12">
            
            {/* Hero Section Ultra Moderne */}
            <div className="text-center mb-12 sm:mb-16 md:mb-20">

              
             
              <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed mb-6 sm:mb-8 px-2">
                Transformez vos idées en <span className="font-bold text-indigo-600">succès</span> avec la plateforme collaborative nouvelle génération
              </p>

    
              
            </div>

            {/* Barre de Recherche Ultra Moderne */}
            <div className="mb-16 sm:mb-20 md:mb-24 py-4 sm:py-5 md:py-6 bg-gradient-to-b from-slate-50 via-blue-50 to-blue-50/0 backdrop-blur-xl">
              <form onSubmit={handleSearchSubmit} className="w-full max-w-4xl mx-auto px-2 sm:px-4">
                <div className="relative group">
                  {/* Background glow effect */}
                  <div className={`
                    absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 
                    rounded-3xl blur-xl opacity-20 transition-opacity duration-300
                    ${isSearchFocused ? 'opacity-40' : 'opacity-20'}
                  `} />
                  
                  {/* Search container */}
                  <div className="relative bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl border border-white/30 shadow-2xl overflow-hidden">
                    <div className="flex items-center w-full">
                      {/* Search icon */}
                      <div className="pl-3 sm:pl-4 md:pl-6 pr-2 sm:pr-3 flex-shrink-0">
                        <Search className={`
                          w-6 h-6 transition-colors duration-300
                          ${isSearchFocused ? 'text-indigo-500' : 'text-gray-400'}
                        `} />
                      </div>
                      
                      {/* Input field */}
                      <input
                        type="text"
                        placeholder="Rechercher..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onFocus={() => setIsSearchFocused(true)}
                        onBlur={() => setIsSearchFocused(false)}
                        className="flex-1 min-w-0 px-2 sm:px-3 md:px-4 py-4 sm:py-5 md:py-6 bg-transparent text-gray-900 placeholder-gray-500 focus:outline-none text-sm sm:text-base md:text-lg"
                      />
                      
                      {/* Search suggestions/filters */}
                      {searchTerm && (
                        <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 md:px-4 flex-shrink-0">
                          <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">{filteredItems.length} résultats</span>
                          <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                          >
                            <X className="w-4 h-4 text-gray-400" />
                          </button>
                        </div>
                      )}
                      
                      {/* Search button */}
                      <button
                        type="submit"
                        disabled={!searchTerm.trim()}
                        className={`
                          m-1 sm:m-2 px-3 sm:px-4 md:px-6 lg:px-8 py-2.5 sm:py-3 md:py-4 rounded-lg sm:rounded-xl md:rounded-2xl font-semibold text-xs sm:text-sm md:text-base transition-all duration-300 flex-shrink-0
                          ${searchTerm.trim() 
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg hover:shadow-xl hover:scale-105' 
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }
                        `}
                      >
                        Rechercher
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Quick search tags */}
                <div className="flex items-center justify-center gap-1 sm:gap-2 md:gap-3 mt-4 sm:mt-5 md:mt-6 flex-wrap px-2 sm:px-3 md:px-0">
                  <span className="text-xs text-gray-500 whitespace-nowrap">Populaires:</span>
                  {['Opportunités','Créer un projet', 'Workspace', 'Boutique en ligne', 'Services'].map((tag, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSearchTerm(tag)}
                      className="px-2.5 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 bg-white/50 hover:bg-white/80 backdrop-blur-md rounded-lg sm:rounded-xl border border-white/30 text-xs text-gray-600 hover:text-gray-900 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </form>
            </div>



            {/* Menu Cards Grid */}
            {filteredItems.length > 0 ? (
              <div className="mb-16 sm:mb-20 md:mb-24 lg:sticky lg:top-[280px]  lg:overflow-y-auto lg:z-30">
                <div className="text-center mb-12 sm:mb-14 md:mb-16 px-2">

                  <p className="text-sm sm:text-base md:text-lg text-gray-600">Accédez à vos outils en un clic</p>
                </div>

                {/* Grid with responsive layout - 4 colonnes sur desktop */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 md:gap-6 px-0">
                  {filteredItems.map((item, index) => (
                    <ModernMenuCard 
                      key={item.id}
                      item={item} 
                      onNavigate={handleNavigate}
                      index={index}
                    />
                  ))}
                </div>
              </div>
            ) : (
              /* Enhanced No Results */
              <div className="mb-16 sm:mb-20 md:mb-24 text-center py-12 sm:py-16 md:py-20">
                <div className="relative mb-6 sm:mb-8 md:mb-10">
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full blur-3xl opacity-20" />
                  <div className="relative inline-flex items-center justify-center w-24 h-24 sm:w-28 md:w-32 lg:h-32 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full">
                    <Search className="w-12 h-12 sm:w-14 md:w-16 text-indigo-500" />
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 md:mb-5">Aucun résultat trouvé</h3>
                <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 max-w-md mx-auto px-4">
                  Essayez avec d'autres mots-clés ou explorez nos catégories populaires
                </p>
                <div className="flex items-center justify-center gap-3 sm:gap-4 md:gap-6 flex-wrap px-4">
                  <button
                    onClick={() => setSearchTerm('')}
                    className="px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-xl sm:rounded-2xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 text-sm sm:text-base md:text-lg"
                  >
                    Réinitialiser la recherche
                  </button>
                  <button
                    onClick={() => navigate('/services')}
                    className="px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 bg-white text-gray-700 font-semibold rounded-xl sm:rounded-2xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 border border-gray-200 text-sm sm:text-base md:text-lg"
                  >
                    Parcourir les services
                  </button>
                </div>
              </div>
            )}

            {/* Stats Section Animées */}
            <div className="mb-24">
              <div className="text-center mb-16 px-2 sm:px-4 md:px-0">
                <h2 className="text-4xl font-bold mb-4">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                    DJAMANN en chiffres
                  </span>
                </h2>
                <p className="text-xl text-gray-600">Une plateforme qui grandit chaque jour</p>
              </div>
              
              <AnimatedStats stats={stats} />
            </div>

            {/* Testimonials Section Redesigned */}
            <div className="mb-24">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                    Ils nous font confiance
                  </span>
                </h2>
                <p className="text-xl text-gray-600">Témoignages authentiques de notre communauté</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    name: 'Amadou Diallo',
                    role: 'Développeur Full-stack',
                    text: 'DJAMANN a transformé ma carrière. Interface intuitive, clients de qualité !',
                    avatar: '👨‍💻',
                    rating: 5,
                    project: '+15 projets réussis'
                  },
                  {
                    name: 'Fatou Sow',
                    role: 'Designer UX/UI',
                    text: 'Communauté incroyable et projets passionnants. Je recommande vivement !',
                    avatar: '👩‍🎨',
                    rating: 5,
                    project: '+12 collaborations'
                  },
                  {
                    name: 'Moussa Koné',
                    role: 'Chef de projet',
                    text: 'Plateforme parfaite pour recruter. J\'ai trouvé mon équipe de rêve !',
                    avatar: '👨‍💼',
                    rating: 5,
                    project: '8 équipes formées'
                  },
                  {
                    name: 'Aïssatou Ba',
                    role: 'Rédactrice',
                    text: 'Missions variées, rémunération correcte. Interface très professionnelle.',
                    avatar: '👩‍💻',
                    rating: 5,
                    project: '+50 articles écrits'
                  }
                ].map((testimonial, idx) => (
                  <div
                    key={idx}
                    className="group relative bg-white/60 backdrop-blur-xl rounded-3xl p-8 border border-white/30 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-2"
                  >
                    {/* Background gradient on hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    <div className="relative z-10">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="text-4xl group-hover:scale-125 transition-transform duration-500">
                          {testimonial.avatar}
                        </div>
                        <div className="flex gap-1">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                      </div>

                      {/* Quote */}
                      <p className="text-gray-700 font-medium mb-6 italic leading-relaxed">
                        "{testimonial.text}"
                      </p>

                      {/* Author Info */}
                      <div className="space-y-2">
                        <p className="font-bold text-gray-900">{testimonial.name}</p>
                        <p className="text-sm text-gray-600">{testimonial.role}</p>
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-full">
                          <Award className="w-3 h-3 text-indigo-500" />
                          <span className="text-xs font-medium text-gray-700">{testimonial.project}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Call to Action Final */}
            <div className="text-center px-2 sm:px-4 md:px-0">
              <div className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-2xl sm:rounded-3xl md:rounded-4xl p-6 sm:p-8 md:p-12 lg:p-16 overflow-hidden shadow-2xl">
                {/* Background effects */}
                <div className="absolute inset-0">
                  <div className="absolute top-0 left-0 w-44 h-44 sm:w-64 md:w-72 bg-white/10 rounded-full blur-3xl animate-pulse" />
                  <div className="absolute bottom-0 right-0 w-48 h-48 sm:w-80 md:w-96 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
                </div>

                <div className="relative z-10 max-w-3xl mx-auto">
                  <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-3 sm:mb-4 md:mb-6">🚀</div>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black text-white mb-3 sm:mb-4 md:mb-6 leading-tight px-2">
                    Prêt à décoller ?
                  </h2>
                  <p className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl text-white/90 mb-6 sm:mb-8 md:mb-10 leading-relaxed px-2 sm:px-4 md:px-6">
                    Rejoignez plus de 50,000 professionnels qui transforment leurs 
                    idées en succès avec DJAMANN. Votre aventure commence maintenant !
                  </p>
                  
                  <div className="flex items-center justify-center gap-3 sm:gap-4 md:gap-6 flex-wrap mb-6 sm:mb-8 md:mb-10 px-2 sm:px-4">
                    <button
                      onClick={() => navigate(user ? '/feed' : '/login')}
                      className="group px-4 sm:px-6 md:px-8 lg:px-10 py-3 sm:py-4 md:py-5 bg-white text-indigo-600 font-bold text-xs sm:text-sm md:text-base lg:text-lg rounded-lg sm:rounded-2xl shadow-xl hover:shadow-2xl hover:scale-110 active:scale-95 transition-all duration-300 whitespace-nowrap"
                    >
                      <span className="flex items-center gap-2 sm:gap-2 md:gap-3">
                        {user ? '🎯 Commencer maintenant' : '✨ Rejoindre DJAMANN'}
                        <ArrowRight className="w-3 h-3 sm:w-4 md:w-5 lg:w-6 group-hover:translate-x-2 transition-transform" />
                      </span>
                    </button>
                    
                    <button
                      onClick={() => navigate('/guide')}
                      className="group px-4 sm:px-6 md:px-8 lg:px-10 py-3 sm:py-4 md:py-5 bg-white/20 backdrop-blur-md text-white font-bold text-xs sm:text-sm md:text-base lg:text-lg rounded-lg sm:rounded-2xl border border-white/30 hover:bg-white/30 transition-all duration-300 whitespace-nowrap"
                    >
                      En savoir plus
                    </button>
                  </div>

                  {/* Social proof */}
                  <div className="flex items-center justify-center gap-2 sm:gap-4 md:gap-6 text-white/80 flex-wrap text-xs px-2 sm:px-4">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 sm:w-5" />
                      <span>50K+ utilisateurs</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 sm:w-5" />
                      <span>4.9/5 étoiles</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Award className="w-4 h-4 sm:w-5" />
                      <span>99% satisfaction</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
